// Печатният документ „Разнасяне на отчет": заглавие с номер и статус
// (Разнесен / Анулиран), банер „Анулиран на … · причина", таблицата и футър
// Анулирай · Коригирай и разнеси повторно | PDF · Excel · Принтирай.
// Действията по документа се показват само когато е отворен от ред и е
// активен (`onAnnul`/`onRepost`); след запис модалът е само за преглед.
//
// `@page { size: A4; margin: 0 }` и собствената двойка posting-doc-paper /
// printing-posting са НАРОЧНО различни от другите документни модали — иначе
// два модула се бият при печат. `autoPrint` печата веднага след отваряне
// („Печат" от реда в справката).
import { useEffect } from "react";
import { Modal } from "antd";
import { CloseOutlined, PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { modalStyles } from "../../../../utils/modalStyles";
import { useViewport } from "../../../../hooks/useViewport";
import { useInventorySettings, useProducts } from "../../../../hooks/useInventory";
import { exportToExcel } from "../../../../utils/excelExport";
import { formatMoney, formatUnitPrice } from "../../components/numbers";
import { formatQty, useUnitDecimals } from "../../components/qty";
import { StatusPill } from "../../components/list/ListBits";
import {
  DANGER_TEXT_BUTTON_CLASS,
  PRIMARY_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
} from "../../components/list/buttonStyles";
import type { ReportPosting } from "../../../../api/inventory";
import {
  buildGrossFactorOf,
  formatDateBg,
  isPostingAnnulled,
} from "./reportPostingShared";

type Props = {
  open: boolean;
  onClose: () => void;
  posting: ReportPosting | null;
  onAnnul?: () => void;
  onRepost?: () => void;
  autoPrint?: boolean;
};

// Печат само на документа: изваждаме .posting-doc-paper в <body>, маркираме
// body с .printing-posting (CSS скрива всичко останало), печатаме и връщаме
// node-а на мястото му.
const printDocument = () => {
  const paper = document.querySelector(".posting-doc-paper") as HTMLElement | null;
  if (!paper || !paper.parentElement) {
    window.print();
    return;
  }
  const parent = paper.parentElement;
  const placeholder = document.createComment("posting-doc-placeholder");
  parent.insertBefore(placeholder, paper);
  document.body.appendChild(paper);
  document.body.classList.add("printing-posting");
  const restore = () => {
    document.body.classList.remove("printing-posting");
    if (placeholder.parentElement) parent.insertBefore(paper, placeholder);
    placeholder.remove();
    window.removeEventListener("afterprint", restore);
  };
  window.addEventListener("afterprint", restore);
  requestAnimationFrame(() => window.print());
};

export const ReportPostingDocumentModal = ({
  open,
  onClose,
  posting,
  onAnnul,
  onRepost,
  autoPrint = false,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useViewport();
  const { data: products = [] } = useProducts();
  const { data: settings } = useInventorySettings();
  const vatRegistered = settings?.vatRegistered ?? true;
  const unitByProduct = new Map(products.map((p) => [p.id, p.unit]));
  const grossFactorOf = buildGrossFactorOf(products, vatRegistered);
  const decimalsOf = useUnitDecimals();

  // „Печат" от реда: модалът се отваря и веднага пуска печата
  useEffect(() => {
    if (!open || !autoPrint || !posting) return;
    const timer = setTimeout(printDocument, 250);
    return () => clearTimeout(timer);
  }, [open, autoPrint, posting]);

  if (!posting) return null;
  const annulled = isPostingAnnulled(posting);
  const width = isMobile ? undefined : isTablet ? 720 : 900;
  const documentLines = posting.lines;
  const totalNet = documentLines.length
    ? documentLines.reduce((sum, line) => sum + (line.sum ?? 0), 0)
    : (posting.totalValueNoVat ?? posting.totalValue ?? 0);
  const totalGross = documentLines.length
    ? documentLines.reduce(
        (sum, line) => sum + (line.sum ?? 0) * grossFactorOf(line.productId),
        0,
      )
    : (posting.totalValueWithVat ?? 0);

  const info: { label: string; value: string }[] = [
    { label: t("inventoryPage.colDate"), value: formatDateBg(posting.date) },
    {
      label: t("inventoryPage.colReportNo"),
      value: posting.reportNumber != null ? `№ ${posting.reportNumber}` : "—",
    },
    {
      label: t("inventoryPage.colProducts"),
      value: String(posting.productsCount ?? posting.lines.length),
    },
  ];
  const subtitle = [
    posting.reportNumber != null
      ? t("inventoryPage.postingReportTitle", { number: posting.reportNumber })
      : null,
    dayjs(posting.createdAt ?? posting.date).format("DD.MM.YYYY HH:mm"),
    posting.createdByName,
  ]
    .filter(Boolean)
    .join(" · ");

  const exportExcel = () =>
    exportToExcel({
      filename: `razneseni-otcheti-${posting.id}`,
      sheetName: t("inventoryPage.postingDocTitle"),
      rows: documentLines,
      columns: [
        { header: t("inventoryPage.colProductName"), value: (l) => l.productName, width: 32 },
        { header: t("inventoryPage.colWarehouse"), value: (l) => l.warehouseName ?? "" },
        { header: t("inventoryPage.colQty"), value: (l) => l.quantity },
        { header: t("inventoryPage.colUnit"), value: (l) => unitByProduct.get(l.productId) ?? "" },
        { header: t("inventoryPage.colUnitPrice"), value: (l) => l.unitPrice },
        { header: t("inventoryPage.colNet"), value: (l) => l.sum },
        { header: t("inventoryPage.colGross"), value: (l) => l.sum * grossFactorOf(l.productId) },
      ],
    });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 20px)" }}
    >
      {/* Печатна механика + таблицата на самия документ — scoped CSS */}
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          body.printing-posting > *:not(.posting-doc-paper) { display: none !important; }
          body.printing-posting { background: #fff !important; }
          body.printing-posting .posting-doc-paper {
            display: block !important;
            position: static !important;
            box-shadow: none !important;
            padding: 14mm 12mm !important;
          }
        }
        .posting-doc table { width: 100%; border-collapse: collapse; }
        .posting-doc th, .posting-doc td {
          border: 1px solid #C9C5BC; padding: 6px 8px; font-size: 12px;
        }
        .posting-doc th {
          background: #f3efe8; text-align: left; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.07em; font-size: 10px;
          color: #8a847b;
        }
        .posting-doc td.num, .posting-doc th.num { text-align: right; font-variant-numeric: tabular-nums; }
        .posting-doc td.unit, .posting-doc th.unit { text-align: center; width: 74px; white-space: nowrap; }
        .posting-doc .annulled-stamp { color: #b42318; font-weight: 700; letter-spacing: 0.1em; }
      `}</style>

      <div className="flex max-h-[90vh] flex-col bg-surface">
        <div
          className={`flex items-start gap-3 border-b border-shade-2 ${
            isMobile ? "px-4 py-3.5" : "px-6 py-[18px]"
          }`}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="text-lg font-semibold leading-[1.2] text-ink">
                {t("inventoryPage.postingDocTitleNo", { number: posting.id })}
              </span>
              <StatusPill tone={annulled ? "neutral" : "ok"}>
                {annulled ? t("inventoryPage.statusAnnulled") : t("inventoryPage.statusPosted")}
              </StatusPill>
            </div>
            <span className="text-[13px] leading-[normal] text-body">{subtitle}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("inventoryPage.close")}
            className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted hover:text-ink"
          >
            <CloseOutlined className="text-sm" />
          </button>
        </div>

        {annulled && (
          <div
            className={`rounded-[9px] bg-fill-2 px-3.5 py-2.5 text-[13.5px] leading-[1.45] text-ink-2 ${
              isMobile ? "mx-4 mt-3.5" : "mx-6 mt-4"
            }`}
          >
            {t("inventoryPage.annulledBanner", {
              date: posting.deletedAt ? dayjs(posting.deletedAt).format("DD.MM.YYYY HH:mm") : "—",
            })}
            {posting.deletedByName ? ` · ${posting.deletedByName}` : ""}
            {posting.deleteReason ? ` · ${posting.deleteReason}` : ""}
          </div>
        )}

        <div className={`min-h-0 overflow-y-auto ${isMobile ? "p-4" : "px-6 py-4"}`}>
          <div className="posting-doc posting-doc-paper bg-surface text-ink">
            <h2 className="mb-3 mt-0 flex items-center gap-3 text-lg font-extrabold leading-[normal] text-ink">
              {t("inventoryPage.postingDocHeading")}
              {annulled && (
                <span className="annulled-stamp text-sm">{t("inventoryPage.statusAnnulledStamp")}</span>
              )}
            </h2>
            <div className={`mb-4 grid gap-2.5 ${isMobile ? "grid-cols-1" : "grid-cols-3"}`}>
              {info.map((item) => (
                <div key={item.label}>
                  <div className="text-[9.5px] font-bold uppercase leading-[normal] tracking-[0.6px] text-muted">
                    {item.label}
                  </div>
                  <div className="mt-0.5 text-[13px] leading-[normal] text-ink">{item.value}</div>
                </div>
              ))}
            </div>
            <table>
              <thead>
                <tr>
                  <th>{t("inventoryPage.colProductName")}</th>
                  <th>{t("inventoryPage.colWarehouse")}</th>
                  <th className="num">{t("inventoryPage.colQty")}</th>
                  <th className="unit">{t("inventoryPage.colUnit")}</th>
                  <th className="num">{t("inventoryPage.colUnitPrice")}</th>
                  <th className="num">{t("inventoryPage.colNet")}</th>
                  <th className="num">{t("inventoryPage.colGross")}</th>
                </tr>
              </thead>
              <tbody>
                {documentLines.map((line) => (
                  <tr key={line.id}>
                    <td>{line.productName}</td>
                    <td>{line.warehouseName || "—"}</td>
                    <td className="num">{formatQty(line.quantity, decimalsOf(line.productId))}</td>
                    <td className="unit">{unitByProduct.get(line.productId) || "—"}</td>
                    {/* ед. цена с до 5 знака — рязането до 2 показва друга цена
                        от записаната (Numeric(13,5)) */}
                    <td className="num">{formatUnitPrice(line.unitPrice)}</td>
                    <td className="num">{formatMoney(line.sum)}</td>
                    <td className="num">{formatMoney(line.sum * grossFactorOf(line.productId))}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={5} className="font-bold">
                    {t("inventoryPage.total")}
                  </td>
                  <td className="num font-extrabold">{formatMoney(totalNet)}</td>
                  <td className="num font-extrabold">{formatMoney(totalGross)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          className={`print:hidden flex flex-wrap items-center gap-2 border-t border-shade-2 ${
            isMobile ? "px-4 py-3" : "px-6 pb-[18px] pt-3.5"
          }`}
        >
          {!annulled && onAnnul && (
            <button type="button" onClick={onAnnul} className={DANGER_TEXT_BUTTON_CLASS}>
              {t("inventoryPage.docDelete")}
            </button>
          )}
          {!annulled && onRepost && (
            <button
              type="button"
              onClick={onRepost}
              className="cursor-pointer rounded-lg border-none bg-transparent px-2.5 py-2 text-[13.5px] leading-[normal] text-ink hover:bg-shade-1"
            >
              {t("inventoryPage.postingRepost")}
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={printDocument}
            title={t("inventoryPage.pdfHint")}
            className={SECONDARY_BUTTON_CLASS}
          >
            PDF
          </button>
          <button type="button" onClick={exportExcel} className={SECONDARY_BUTTON_CLASS}>
            Excel
          </button>
          <button type="button" onClick={printDocument} className={PRIMARY_BUTTON_CLASS}>
            <PrinterOutlined className="text-[15px]" />
            {t("inventoryPage.print")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ReportPostingDocumentModal;
